"use client"
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useFormikContext } from "formik";
import debounce from "lodash.debounce";
import isEqual from "lodash.isequal";

const AutoSave = ({
  preventAutoSave = false,
  debounceDelay = 2000,
  callback,
  showTimestamp = false,
  debug = false
}) => {
  const {
    values,
    touched,
    dirty,
    isValid,
    submitForm,
    isSubmitting,
    errors,
    resetForm
  } = useFormikContext();

  const [lastSaved, setLastSaved] = useState(null);
  const hasInitialized = useRef(false);
  const hasUserInteraction = useRef(false);
  const previousTouched = useRef({});

  // These refs are updated synchronously during render so the debounced callback
  // always reads the most recent values — no risk of the debounce firing in the
  // gap between a render and a subsequent useEffect that would update a stale ref.
  const currentState = useRef({ dirty: false, isValid: false, preventAutoSave: false });
  currentState.current = { dirty, isValid, preventAutoSave };

  // Keep stable refs for submitForm and callback so debouncedSave doesn't need
  // to list them as useCallback deps. Without this, every Formik re-render after
  // a submit recreates submitForm → recreates debouncedSave → the [touched,
  // debouncedSave] effect fires again → schedules a new debounce even though the
  // user hasn't interacted, causing a spurious save loop.
  const submitFormRef = useRef(submitForm);
  submitFormRef.current = submitForm;
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const debouncedSave = useCallback(
    debounce(() => {
      const {
        dirty: currentDirty,
        isValid: currentIsValid,
        preventAutoSave: currentPreventAutoSave
      } = currentState.current;

      if (
        currentDirty &&
        currentIsValid &&
        !currentPreventAutoSave &&
        hasUserInteraction.current
      ) {
        submitFormRef.current()
          .then(() => {
            callbackRef.current?.(new Date(), true);
          })
          .catch(() => {
            callbackRef.current?.(null, false);
          });
      }
    }, debounceDelay),
    // debounceDelay is the only true dependency; submitForm/callback are accessed
    // via refs so changing them does not recreate (and therefore re-trigger) the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounceDelay]
  );

  // Trigger save when touched state changes (user interaction).
  // debouncedSave is now stable (only recreated when debounceDelay changes) so
  // it is safe to include in the deps array without causing spurious re-runs.
  useEffect(() => {
    // Skip the first render to prevent initial auto-save
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      return;
    }

    // Update hasUserInteraction if any fields are touched
    const touchedKeys = Object.keys(touched);
    if (touchedKeys.length > 0) {
      hasUserInteraction.current = true;
    }

    // Trigger the debounced save
    debouncedSave();

    return debouncedSave.cancel;
  }, [touched, debouncedSave]);

  return (
    <>
      <div
        className="auto-save"
        style={showTimestamp ? { display: "block" } : { display: "none" }}
      >
        {isSubmitting
          ? "saving..."
          : lastSaved !== null
          ? `Last Saved: ${lastSaved}`
          : null}
      </div>
      {debug && (
        <div className="auto-save-debug" style={debugStyles}>
          <h4>Formik Debug</h4>
          <pre>
            <strong>Prevent Auto Save:</strong>{" "}
            {JSON.stringify(preventAutoSave, null, 2)}
          </pre>
          <pre>
            <strong>Has User Interaction:</strong>{" "}
            {JSON.stringify(hasUserInteraction.current, null, 2)}
          </pre>
          <pre>
            <strong>Dirty:</strong> {JSON.stringify(dirty, null, 2)}
          </pre>
          <pre>
            <strong>isValid:</strong> {JSON.stringify(isValid, null, 2)}
          </pre>
          <pre>
            <strong>Errors:</strong> {JSON.stringify(errors, null, 2)}
          </pre>
          <pre>
            <strong>Touched:</strong> {JSON.stringify(touched, null, 2)}
          </pre>
          <pre>
            <strong>Previous Touched:</strong>{" "}
            {JSON.stringify(previousTouched.current, null, 2)}
          </pre>
          <pre>
            <strong>Values:</strong> {JSON.stringify(values, null, 2)}
          </pre>
          <pre>
            <strong>Current State Ref:</strong>{" "}
            {JSON.stringify(currentState.current, null, 2)}
          </pre>
        </div>
      )}
    </>
  );
};

// Simple debug overlay styles
const debugStyles = {
  position: "fixed",
  bottom: 0,
  right: 0,
  width: "300px",
  maxHeight: "50vh",
  overflowY: "auto",
  backgroundColor: "rgba(0, 0, 0, 0.85)",
  color: "white",
  fontSize: "12px",
  padding: "10px",
  zIndex: 19999,
  borderTopLeftRadius: "8px",
  fontFamily: "monospace"
};

export default AutoSave;
